/**
 * The columns picker: a checkbox list, everything on to begin with.
 *
 * WHY IT IS NOT `Menu`. A menu closes when a row is chosen, which is right for
 * a menu and wrong here: turning three columns off would mean opening the panel
 * three times. The rows are also genuinely checkboxes rather than menu items,
 * and a `menuitemradio` announcing itself where several answers are allowed is
 * a worse lie than a plain list of checkboxes.
 *
 * The first column and `fixed` columns are never offered. `pickableColumns` in
 * `Table.tsx` decides that, and `DataTable` hands the result in as `choices`.
 *
 * THE ROWS ARE IN THE TABLE'S ORDER. A header can be dragged to a new place,
 * and a drag is a pointer's way of doing it, so each row here also steps its
 * column left or right with two small buttons, which a keyboard reaches. A
 * reset under the list puts the order and the widths back, shown only while
 * there is something to put back. Which columns are hidden is not reset by it:
 * that choice is made right here, one box at a time.
 */
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { useStableId } from '../lib/hooks'
import { Button, IconButton } from './Button'
import { toggleHidden } from './column-picker-model'
import { IconChevronDown, IconChevronUp, IconColumns, IconPlus } from './icons'
import { Checkbox, Select } from './Input'
import type { SelectOption } from './Input'
import { TablePopover, usePopover } from './TablePopover'

export interface ColumnChoice {
  key: string
  label: ReactNode
  /** One line under the label saying what the column holds. */
  description?: ReactNode
}

export interface ColumnPickerProps {
  choices: readonly ColumnChoice[]
  hidden: readonly string[]
  onChange: (next: string[]) => void
  /** Moves a column one place left (`-1`) or right (`1`). Left out, the rows have no step buttons. */
  onStep?: (key: string, step: -1 | 1) => void
  /** Puts the order and the widths back. Left out while there is nothing to put back. */
  onReset?: () => void
  className?: string
}

/** Measured: a 240 wide panel, 4 of padding, a 36 tall row per column. */
const PANEL_WIDTH = 240

export function ColumnPicker({ choices, hidden, onChange, onStep, onReset, className }: ColumnPickerProps) {
  const popover = usePopover()
  const groupId = useStableId('columns')

  return (
    <>
      <IconButton
        ref={popover.triggerRef}
        label="Columns"
        variant="secondary"
        size="sm"
        aria-haspopup="dialog"
        aria-expanded={popover.open}
        onClick={popover.toggle}
        className={className}
      >
        <IconColumns className="h-5 w-5" />
      </IconButton>

      <TablePopover
        open={popover.open}
        onClose={popover.close}
        triggerRef={popover.triggerRef}
        width={PANEL_WIDTH}
        align="end"
        label="Columns"
        className="rounded-lg p-1 shadow-lg"
      >
        <ul className="min-h-0 flex-1 overflow-y-auto">
          {choices.map((choice, at) => {
            const id = `${groupId}-${choice.key}`
            const on = !hidden.includes(choice.key)
            return (
              <li key={choice.key} className={cn(onStep !== undefined && 'flex items-start gap-0.5')}>
                {/*
                  The whole row is the label, so the click target is the 36px
                  row and not the 16px box. Without the id pairing, a click on
                  the words would do nothing, which is the small broken thing
                  nobody reports.
                */}
                <label
                  htmlFor={id}
                  className={cn(
                    'flex min-w-0 flex-1 cursor-pointer gap-2 rounded-md px-2.5',
                    'text-base font-medium text-ink-secondary hover:bg-surface-hover',
                    choice.description === undefined ? 'h-9 items-center' : 'items-start py-2',
                  )}
                >
                  <Checkbox
                    id={id}
                    className={choice.description === undefined ? 'mt-0' : 'mt-0.5'}
                    checked={on}
                    onChange={() => onChange(toggleHidden(hidden, choice.key))}
                  />
                  {choice.description === undefined ? (
                    <span className="min-w-0 truncate">{choice.label}</span>
                  ) : (
                    <span className="min-w-0">
                      <span className="block truncate">{choice.label}</span>
                      <span className="mt-0.5 block text-sm font-normal text-ink-muted">{choice.description}</span>
                    </span>
                  )}
                </label>
                {onStep === undefined ? null : (
                  <span className="flex h-9 shrink-0 items-center">
                    <IconButton
                      label={`Move ${typeof choice.label === 'string' ? choice.label : 'this column'} left`}
                      variant="ghost"
                      size="2xs"
                      disabled={at === 0}
                      onClick={() => onStep(choice.key, -1)}
                    >
                      <IconChevronUp className="h-4 w-4" />
                    </IconButton>
                    <IconButton
                      label={`Move ${typeof choice.label === 'string' ? choice.label : 'this column'} right`}
                      variant="ghost"
                      size="2xs"
                      disabled={at === choices.length - 1}
                      onClick={() => onStep(choice.key, 1)}
                    >
                      <IconChevronDown className="h-4 w-4" />
                    </IconButton>
                  </span>
                )}
              </li>
            )
          })}
        </ul>
        {onReset === undefined ? null : (
          <div className="border-t border-line px-1 pt-1">
            <Button variant="ghost" size="sm" className="w-full justify-start" onClick={onReset}>
              Reset order and widths
            </Button>
          </div>
        )}
      </TablePopover>
    </>
  )
}

export interface AddColumnButtonProps {
  /** The variables that can become a column. */
  options: readonly SelectOption[]
  onAdd: (value: string) => void
}

/**
 * The `+ Add column` header cell: a link button opening a small panel with one
 * select. Picking a variable adds its column and closes the panel. Put it in
 * the header of a `Column` with `kind: 'add'`.
 */
export function AddColumnButton({ options, onAdd }: AddColumnButtonProps) {
  const popover = usePopover()
  const id = useStableId('add-column')
  return (
    <>
      <Button
        ref={popover.triggerRef}
        variant="link"
        size="sm"
        className="h-auto px-0"
        aria-haspopup="dialog"
        aria-expanded={popover.open}
        onClick={popover.toggle}
        leadingIcon={<IconPlus className="h-4 w-4" />}
      >
        Add column
      </Button>
      <TablePopover
        open={popover.open}
        onClose={popover.close}
        triggerRef={popover.triggerRef}
        width={280}
        label="Add column"
        className="gap-2 rounded-lg p-3 shadow-lg"
      >
        <label htmlFor={id} className="text-base font-medium text-ink-secondary">
          Select variables to show variable column
        </label>
        <Select
          id={id}
          selectSize="sm"
          placeholder="Choose a variable"
          value=""
          onChange={(event) => {
            if (event.target.value === '') return
            onAdd(event.target.value)
            popover.close()
          }}
          options={[...options]}
        />
      </TablePopover>
    </>
  )
}
